import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { AvatarStack } from '../components/ui/avatar';
import { ProgressRing, getProgressStage } from '../components/ui/progress-ring';
import { SectionsNav } from '../components/ui/sections-nav';
import { CollaborativeCanvas } from '../components/ui/collaborative-canvas';
import { MaryChat } from '../components/ui/mary-chat';
import { InviteDialog } from '../components/ui/invite-dialog';
import { SummaryExporter } from '../components/ui/summary-exporter';
import { toast } from 'sonner';
import { 
  SeedIcon, UserAddIcon, ScrollIcon, BackIcon, ChatIcon, 
  EditIcon, MenuIcon, CloseIcon 
} from '../components/ui/icons';
import { EcosystemNav } from '../components/ui/ecosystem-nav';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export default function CharterStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState('belonging');
  const [isLoading, setIsLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [mobileView, setMobileView] = useState('canvas');
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [insertedContent, setInsertedContent] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const userName = localStorage.getItem('blissabode_user_name') || 'Anonymous';

  const loadProject = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`);
      if (!response.ok) throw new Error('Failed to load project');
      
      const data = await response.json();
      setProject(data.project);
      setSections(data.sections || []);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load project');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId, loadProject]);

  // WebSocket connection
  useEffect(() => {
    if (!projectId || !userName) return;

    const connectWebSocket = () => {
      const ws = new WebSocket(`${WS_URL}/ws/projects/${projectId}?user_name=${encodeURIComponent(userName)}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'presence') {
            setOnlineUsers(data.data.users || []);
          } else if (data.type === 'section_update') {
            setSections(prev => prev.map(s => 
              s.key === data.data.section_key
                ? { ...s, content: data.data.content, updated_by: data.data.updated_by }
                : s
            ));
          }
        } catch (error) {
          console.error('WebSocket message error:', error);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [projectId, userName]);

  const currentSection = sections.find(s => s.key === activeSection);

  const calculateProgress = () => {
    if (sections.length === 0) return 0;
    const totalWords = sections.reduce((acc, s) => {
      const words = (s.content || '').trim().split(/\s+/).filter(w => w).length;
      return acc + words;
    }, 0);
    return Math.min(100, Math.round((totalWords / 1600) * 100));
  };

  const overallProgress = calculateProgress();
  const { stage: progressStage, emoji: progressEmoji } = getProgressStage(overallProgress);

  const handleContentChange = (content) => {
    setSections(prev => prev.map(s =>
      s.key === activeSection ? { ...s, content } : s
    ));
  };

  const handleInsertContent = (content) => {
    setInsertedContent(content);
  };

  const handleInsertComplete = () => {
    setInsertedContent(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--sky)] flex items-center justify-center">
            <SeedIcon size={32} className="text-[var(--primary)] animate-pulse" />
          </div>
          <p className="text-[var(--fg)]/60">Loading your charter...</p>
        </motion.div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur bg-[var(--bg)]/90 border-b border-[var(--muted)]/50" data-testid="app-header">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 -ml-2 hover:bg-[var(--muted)]/50 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <BackIcon size={20} className="text-[var(--fg)]/60" />
              </button>
              <div className="hidden sm:flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center">
                  <SeedIcon size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-lg font-semibold text-[var(--fg)] truncate max-w-[200px] md:max-w-none">
                    {project.name}
                  </h1>
                  <p className="text-xs text-[var(--fg)]/60">
                    {progressStage} {progressEmoji} • {overallProgress}% complete
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMobileNav(!showMobileNav)}
                className="sm:hidden p-2 hover:bg-[var(--muted)]/50 rounded-lg"
              >
                {showMobileNav ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
              </button>
            </div>

            <div className="hidden lg:flex items-center gap-3" data-testid="header-progress">
              <ProgressRing size={40} stroke={4} progress={overallProgress} />
              <div>
                <p className="text-sm font-medium text-[var(--fg)]">{overallProgress}%</p>
                <p className="text-xs text-[var(--fg)]/60">{progressStage}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {onlineUsers.length > 0 && (
                <div data-testid="header-presence-stack" className="hidden sm:block">
                  <AvatarStack users={onlineUsers} max={4} />
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInvite(true)}
                className="hidden sm:flex"
                data-testid="header-invite-button"
              >
                <UserAddIcon size={16} />
                Invite
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSummary(true)}
                data-testid="header-summary-button"
              >
                <ScrollIcon size={16} />
                <span className="hidden sm:inline">Summary</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {showMobileNav && (
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          className="fixed inset-0 z-40 bg-[var(--bg)] sm:hidden"
        >
          <div className="p-4 h-full overflow-y-auto">
            <SectionsNav
              sections={sections}
              activeSection={activeSection}
              onSectionSelect={(key) => {
                setActiveSection(key);
                setShowMobileNav(false);
              }}
            />
          </div>
        </motion.div>
      )}

      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_360px] gap-6 h-[calc(100vh-140px)]">
          <div className="hidden lg:block">
            <SectionsNav
              sections={sections}
              activeSection={activeSection}
              onSectionSelect={setActiveSection}
            />
          </div>

          <div className="bg-[var(--card)] rounded-[var(--radius-lg)] border border-[var(--muted)]/50 p-6 overflow-hidden flex flex-col">
            <CollaborativeCanvas
              projectId={projectId}
              section={currentSection}
              userName={userName}
              onContentChange={handleContentChange}
              insertedContent={insertedContent}
              onInsertComplete={handleInsertComplete}
            />
          </div>

          <div className="hidden lg:block h-full">
            <MaryChat
              projectId={projectId}
              sectionKey={activeSection}
              sectionContent={currentSection?.content}
              userName={userName}
              onInsertContent={handleInsertContent}
            />
          </div>
        </div>

        {/* Mobile tab bar */}
        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-[var(--card)] border-t border-[var(--muted)]/50 p-2 flex gap-2">
          <button
            onClick={() => setShowMobileNav(true)}
            className={`flex-1 py-3 rounded-lg flex flex-col items-center gap-1 transition-colors ${mobileView === 'nav' ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'text-[var(--fg)]/60'}`}
          >
            <MenuIcon size={20} />
            <span className="text-xs">Sections</span>
          </button>
          <button
            onClick={() => setMobileView('canvas')}
            className={`flex-1 py-3 rounded-lg flex flex-col items-center gap-1 transition-colors ${mobileView === 'canvas' ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'text-[var(--fg)]/60'}`}
          >
            <EditIcon size={20} />
            <span className="text-xs">Edit</span>
          </button>
          <button
            onClick={() => setMobileView('chat')}
            className={`flex-1 py-3 rounded-lg flex flex-col items-center gap-1 transition-colors ${mobileView === 'chat' ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'text-[var(--fg)]/60'}`}
          >
            <ChatIcon size={20} />
            <span className="text-xs">M.ai.ry</span>
          </button>
        </div>

        {/* Mobile chat panel */}
        {mobileView === 'chat' && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed inset-x-0 bottom-16 top-20 z-30 lg:hidden p-4 bg-[var(--bg)]"
          >
            <MaryChat
              projectId={projectId}
              sectionKey={activeSection}
              sectionContent={currentSection?.content}
              userName={userName}
              onInsertContent={handleInsertContent}
            />
          </motion.div>
        )}
      </main>

      <InviteDialog
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        projectId={projectId}
        joinCode={project.join_code}
      />

      <SummaryExporter
        isOpen={showSummary}
        onClose={() => setShowSummary(false)}
        projectId={projectId}
        projectName={project.name}
        sections={sections}
      />

      <EcosystemNav currentSite="community-forge" />
    </div>
  );
}
